import { useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { userAtom } from '../atoms/userAtom';
import { useEventSource } from '@reactuses/core';

type ReceivedEvent = {
  receivedAtMs: number;
  rawData: string | null;
  parsed?: unknown;
  lastEventId: string | null;
  eventName: string | null;
};

const UserEvents = () => {
  const authenticatedUser = useAtomValue(userAtom);
  const [events, setEvents] = useState<ReceivedEvent[]>([]);

  const sseUrl = useMemo(() => (
    authenticatedUser?.id ? `${import.meta.env.VITE_SSE_SUB_URL}/${authenticatedUser?.id}` : ''
  ), [authenticatedUser?.id]);

  // TODO: Implement our own exponential backoff reconnect
  // based https://github.com/childrentime/reactuse/blob/main/packages/core/src/useEventSource/index.ts
  const { status, data, error, lastEventId, event, open, close } = useEventSource(
    sseUrl,
    [],
    {
      immediate: false,
      autoReconnect: {
        retries: () => true, // infinite retries
        delay: 3000,
        onFailed: () => {
          console.log('Failed to reconnect');
        },
      },
      withCredentials: true,
    }
  );

  // Connect/disconnect based on auth state
  useEffect(() => {
    if (sseUrl) {
      open();
    } else {
      close();
    }
    // Cleanup on unmount
    return () => {
      close();
    };
  }, [sseUrl, open, close]);

  // Append incoming data to the list
  useEffect(() => {
    if (data == null && error == null) return;
    setEvents(prev => {
      const next: ReceivedEvent = {
        receivedAtMs: Date.now(),
        rawData: data ?? null,
        lastEventId,
        eventName: event,
        parsed: (() => {
          try {
            return data ? JSON.parse(data) : undefined;
          } catch {
            return undefined;
          }
        })()
      };
      return [next, ...prev].slice(0, 200);
    });
  }, [data, error, lastEventId, event]);

  return (
    <div style={{ padding: 12 }}>
      <div style={{ marginBottom: 8 }}>
        <strong>EventSource status:</strong> {status}
        {error ? <span style={{ color: 'crimson', marginLeft: 8 }}>error</span> : null}
      </div>
      <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
        Channel: {authenticatedUser?.id ?? '—'}
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {events.length === 0 ? (
          <div style={{ color: '#999' }}>No events yet</div>
        ) : (
          events.map((e, i) => (
            <div key={e.lastEventId || i} style={{ border: '1px solid #eee', borderRadius: 6, padding: 8 }}>
              <div style={{ fontSize: 12, color: '#777' }}>
                {new Date(e.receivedAtMs).toLocaleTimeString()} — id: {e.lastEventId ?? '-'} — event: {e.eventName ?? 'message'}
              </div>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {e.rawData}
              </pre>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default UserEvents;


